// src/pages/SubscriptionsPage.jsx
//
// Admin > Subscriptions — activate/deactivate module subscriptions for the
// currently-selected Account. Real backend
// (architecture_scaffold/presentation/shared/subscription/subscription_controller.py):
// list/activate/deactivate a single module, or activate a whole tier at
// once. Gated by subscriptions:manage (see navConfig.jsx/AppRoutes.jsx) —
// only org/super admins hold that permission.
//
// Scoped to whichever Account is selected in the topbar, same pattern as
// accounts/pages/ProjectsPage.jsx, rather than building a second
// account-picker.

import { useCallback, useEffect, useState } from "react";
import { Boxes, Layers, Power, Zap } from "lucide-react";
import { PageHeader, Button, EmptyState, Alert, StatusBadge, Modal, FormField, Select, DatePicker, ConfirmModal, SkeletonCardGrid, useDiscardConfirm } from "../shared/ui";
import { useWorkspace } from "../context/WorkspaceContext";
import { subscriptionApi } from "../api/subscriptionApi";
import { getApiError } from "../api/client";

const MODULE_LABELS = {
  sow_analysis: "SOW Analysis",
  codebase: "Codebase Analysis",
  rds: "RDS",
  golive: "GoLive",
  compliance: "Compliance",
  it_service: "IT Service",
  adai: "ADAI (Documentation)",
  mcp_agent: "MCP Agent",
  conductor: "Conductor",
};

// Tiers a whole bundle can be activated at. INDIVIDUAL is deliberately not
// offered here — it's the classification a hand-picked single-module
// activation gets (see ModuleActivateModal below), not a bundle an admin
// would pick from this list; selecting it here would only grant Conductor
// (SubscriptionTier.INDIVIDUAL.default_modules()'s fallback), which isn't
// what "activate a tier" should ever do.
const TIER_OPTIONS = [
  { value: "STARTER", label: "Starter" },
  { value: "GROWTH", label: "Growth" },
  { value: "ENTERPRISE", label: "Enterprise" },
];

// Mirrors domain/shared/subscription/value_objects/subscription_tier.py's
// SubscriptionTier.default_modules() — kept in sync by hand since the
// frontend has no access to that enum. ENTERPRISE maps to every module
// (ModuleName.full_package()), so it's left out here and handled as "All
// modules" instead of duplicating the full module list a second time.
const TIER_MODULES = {
  STARTER: ["sow_analysis", "codebase", "conductor"],
  GROWTH: ["sow_analysis", "codebase", "conductor", "golive", "rds", "compliance"],
};

function describeTierModules(tier) {
  const modules = TIER_MODULES[tier];
  if (!modules) return "All modules.";
  return `${modules.map((m) => MODULE_LABELS[m] || m).join(", ")}.`;
}

// Single-module activation. No Tier field — the backend always classifies
// a hand-picked single module as SubscriptionTier.INDIVIDUAL, so there's
// nothing meaningful for an admin to choose here; picking, say, "Starter"
// for one module wouldn't activate the rest of the Starter bundle, it
// would just mislabel this one module's tier.
function ModuleActivateModal({ open, title, onClose, onConfirm, submitting }) {
  const [expiresAt, setExpiresAt] = useState("");
  const [dirty, setDirty] = useState(false);

  // A successful activate closes this via the parent calling `onClose`
  // directly (not `handleClose`), so `dirty` must also clear on that path
  // -- watch the open prop's true -> false transition rather than only
  // resetting inside handleClose.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) setDirty(false);
  }

  function handleClose() {
    setExpiresAt("");
    setDirty(false);
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty, handleClose);

  return (
    <>
      <Modal open={open} title={title} onClose={requestClose}>
        <div className="flex flex-col gap-4">
          <FormField label="Expires" helperText="Optional — leave blank for a non-expiring subscription.">
            <DatePicker
              value={expiresAt}
              onChange={(iso) => {
                setExpiresAt(iso);
                setDirty(true);
              }}
            />
          </FormField>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
            <Button
              type="button"
              variant="primary"
              loading={submitting}
              onClick={() => onConfirm({ expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null })}
            >
              Activate
            </Button>
          </div>
        </div>
      </Modal>
      {confirmDialog}
    </>
  );
}

// Bundle activation — grants every module the selected tier includes.
function TierActivateModal({ open, onClose, onConfirm, submitting }) {
  const [tier, setTier] = useState(TIER_OPTIONS[0].value);
  const [expiresAt, setExpiresAt] = useState("");
  const [dirty, setDirty] = useState(false);

  // Same as ModuleActivateModal: a successful activate closes this via the
  // parent calling `onClose` directly, so clear `dirty` on that transition
  // too, not just inside handleClose.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) setDirty(false);
  }

  function handleClose() {
    setTier(TIER_OPTIONS[0].value);
    setExpiresAt("");
    setDirty(false);
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty, handleClose);

  return (
    <>
      <Modal open={open} title="Activate a tier" onClose={requestClose}>
        <div className="flex flex-col gap-4">
          <FormField label="Tier" helperText={`Activates: ${describeTierModules(tier)}`}>
            <Select
              options={TIER_OPTIONS}
              value={tier}
              onChange={(e) => {
                setTier(e.target.value);
                setDirty(true);
              }}
            />
          </FormField>
          <FormField label="Expires" helperText="Optional — leave blank for a non-expiring subscription.">
            <DatePicker
              value={expiresAt}
              onChange={(iso) => {
                setExpiresAt(iso);
                setDirty(true);
              }}
            />
          </FormField>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
            <Button
              type="button"
              variant="primary"
              loading={submitting}
              onClick={() => onConfirm({ tier, expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null })}
            >
              Activate
            </Button>
          </div>
        </div>
      </Modal>
      {confirmDialog}
    </>
  );
}

export default function SubscriptionsPage() {
  const { currentAccount, currentAccountId } = useWorkspace();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyModule, setBusyModule] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [activateModuleTarget, setActivateModuleTarget] = useState(null);
  const [tierModalOpen, setTierModalOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (currentAccountId == null) {
      setModules([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await subscriptionApi.listModules(currentAccountId);
      setModules(data.modules);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  }, [currentAccountId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handleActivate({ expiresAt }) {
    setBusyModule(activateModuleTarget);
    try {
      await subscriptionApi.activateModule(currentAccountId, activateModuleTarget, {
        tier: "INDIVIDUAL",
        expiresAt,
      });
      await refresh();
      setActivateModuleTarget(null);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setBusyModule(null);
    }
  }

  async function handleActivateTier({ tier, expiresAt }) {
    setBusyModule("__tier__");
    try {
      await subscriptionApi.activateTier(currentAccountId, tier, { expiresAt });
      await refresh();
      setTierModalOpen(false);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setBusyModule(null);
    }
  }

  async function handleDeactivate(moduleName) {
    setBusyModule(moduleName);
    try {
      await subscriptionApi.deactivateModule(currentAccountId, moduleName);
      await refresh();
      setConfirmDeactivate(null);
    } finally {
      setBusyModule(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="Module Subscriptions"
        subtitle={
          currentAccount
            ? <>Activate or deactivate modules for <span className="font-medium text-ink">{currentAccount.name}</span>. Switch Accounts in the topbar to manage another Account.</>
            : "Select an Account in the topbar to manage its module subscriptions."
        }
        actions={
          currentAccount && (
            <Button variant="primary" onClick={() => setTierModalOpen(true)}>
              <Zap size={15} /> Activate tier
            </Button>
          )
        }
      />

      {error && (
        <Alert variant="danger" title="Something went wrong" className="mb-4">
          {error}
        </Alert>
      )}

      {!currentAccount ? (
        <EmptyState icon={Boxes} title="No Account selected" description="Choose an Account from the topbar switcher first." />
      ) : loading ? (
        <SkeletonCardGrid cards={6} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((m) => {
            const isBusy = busyModule === m.name;
            return (
              <div key={m.name} className="bg-white border border-line rounded-xl p-4 flex flex-col gap-3 shadow-[var(--jade-sh-1)]">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center rounded-full bg-brand-50 text-brand-700 shrink-0" style={{ width: 32, height: 32 }}>
                    <Layers size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink truncate">{MODULE_LABELS[m.name] || m.name}</p>
                    {m.always_included && <p className="text-xs text-muted mt-0.5">Always included</p>}
                  </div>
                  <StatusBadge map="accountStatus" status={m.active ? "active" : "inactive"} size="sm" />
                </div>

                <div className="flex items-center justify-end gap-1.5 pt-1 border-t border-line">
                  {m.always_included ? (
                    <span className="text-[11px] text-muted">Can't be changed</span>
                  ) : m.active ? (
                    <Button variant="ghost" size="sm" disabled={isBusy} onClick={() => setConfirmDeactivate(m.name)}>
                      <Power size={13} /> Deactivate
                    </Button>
                  ) : (
                    <Button variant="secondary" size="sm" disabled={isBusy} onClick={() => setActivateModuleTarget(m.name)}>
                      <Zap size={13} /> Activate
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ModuleActivateModal
        open={Boolean(activateModuleTarget)}
        title={`Activate ${MODULE_LABELS[activateModuleTarget] || activateModuleTarget}`}
        onClose={() => setActivateModuleTarget(null)}
        onConfirm={handleActivate}
        submitting={busyModule === activateModuleTarget}
      />

      <TierActivateModal
        open={tierModalOpen}
        onClose={() => setTierModalOpen(false)}
        onConfirm={handleActivateTier}
        submitting={busyModule === "__tier__"}
      />

      <ConfirmModal
        open={Boolean(confirmDeactivate)}
        entityLabel="module"
        name={MODULE_LABELS[confirmDeactivate] || confirmDeactivate}
        onClose={() => setConfirmDeactivate(null)}
        onConfirm={() => handleDeactivate(confirmDeactivate)}
      >
        Everyone on {currentAccount?.name} loses access to this module until it's reactivated.
      </ConfirmModal>
    </div>
  );
}
